import Grid from '@mui/material/Grid';

import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import type {
  Contentful,
  ContentStyle,
  FrontifyImageContent,
  LinkContent,
} from '@/contentful/contentful-exports';
import useViewPortSize from '@/hooks/useViewportSize';
import { ResponsiveTypeVariant } from '@/styles/global.styles';
import { ViewportSize } from '@/styles/viewportSize';
import { getFrontifyBackgroundImageUrl } from '@/utils/frontify';

import useStyles from './ActionBannerFull.styles';

export enum FrameStyle {
  LIGHT = 'Light',
  DARK = 'Dark',
}
export interface ActionBannerFullContent extends Contentful {
  fields: {
    backgroundImages: FrontifyImageContent[];
    style: ContentStyle;
    frameStyle: FrameStyle;
    heading?: string;
    subheading?: string;
    secondaryCta: LinkContent;
    tertiaryCta?: LinkContent;
  };
}

interface ActionBannerFullProps {
  content: ActionBannerFullContent;
}

export default function ActionBannerFull({
  content: {
    fields: {
      backgroundImages,
      style,
      frameStyle,
      heading,
      subheading,
      secondaryCta,
      tertiaryCta,
    },
  },
}: ActionBannerFullProps) {
  const backgroundImageUrls = {
    xsBackgroundImage: getFrontifyBackgroundImageUrl(
      backgroundImages[1],
      ViewportSize.sm
    ),
    mdBackgroundImage: getFrontifyBackgroundImageUrl(
      backgroundImages[0],
      ViewportSize.xl
    ),
  };
  const { classes, cx } = useStyles(backgroundImageUrls);
  const { isExtraSmall, isSmall } = useViewPortSize();
  const isXsOrSm = isExtraSmall || isSmall;

  const isContentOnDark = style === 'OnDark';

  // biome-ignore lint/correctness/noNestedComponentDefinitions: closes over per-render props
  // biome-ignore lint/correctness/noComponentHookFactories: closes over per-render props
  const BannerContent = () => (
    <Grid
      size={{
        xs: 12,
        sm: 9,
        md: 5,
      }}
    >
      <div className={classes.content}>
        <SectionHeader
          heading={heading}
          subheading={subheading}
          isOnDark={isContentOnDark}
          fixed
          subheadingClassName={classes.sectionHeaderSubheading}
          subheadingFontStyle={ResponsiveTypeVariant.body}
        />
        <div className={classes.buttonContainer}>
          <Button
            label={secondaryCta.fields.text}
            href={secondaryCta.fields.url}
            size={ButtonSize.MD}
            isOnDark={isContentOnDark}
            variant={ButtonVariant.SECONDARY}
          />
          {tertiaryCta && (
            <Button
              label={tertiaryCta.fields.text}
              href={tertiaryCta.fields.url}
              size={ButtonSize.MD}
              isOnDark={isContentOnDark}
              variant={ButtonVariant.TERTIARY}
            />
          )}
        </div>
      </div>
    </Grid>
  );

  return (
    <div className={cx(classes.container, frameStyle)}>
      <GridContainer fixed gridProps={{ className: classes.relativePosition }}>
        <Grid size={12}>
          <div className={classes.banner} />
        </Grid>
        {/* The GridContainer below is absolutely positioned within the banner's outer fixed div on large screen sizes */}
        {/* in order to use column width for the content and vertically center within banner */}
        {!isXsOrSm && (
          <GridContainer
            fixed
            gridProps={{ className: classes.absolutePosition }}
          >
            <Grid size={1} />
            <BannerContent />
          </GridContainer>
        )}
      </GridContainer>
      {/* Mobile lower area content */}
      {isXsOrSm && (
        <GridContainer
          fixed
          gridProps={{ className: classes.relativePosition }}
        >
          {/* Similar pattern to above except for the content sets the width and the banner is absolute */}
          <GridContainer
            fixed
            gridProps={{ className: classes.absolutePosition }}
          >
            <Grid size={12}>
              <div className={cx(classes.mobileLowerBanner, style)} />
            </Grid>
          </GridContainer>
          <BannerContent />
        </GridContainer>
      )}
    </div>
  );
}
